import { useState, useEffect } from 'react';
import { Wallet, Loader2, Search, Download, CreditCard, ArrowUpRight, ArrowDownLeft, Eye, Plus, Clock, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

export default function AdminWallets() {
  const { token, user } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creditDialog, setCreditDialog] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [crediting, setCrediting] = useState(false);
  const [txnDialog, setTxnDialog] = useState(null);
  const [txnData, setTxnData] = useState(null);
  const [txnLoading, setTxnLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [reconcileStatus, setReconcileStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // { txn, mode: 'approve'|'reject' }
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const canManageWallet = ['super_admin', 'manager', 'finance', 'admin'].includes(user?.role)
    || (user?.permission_overrides || []).some(p => p === 'wallet' || p === '*');

  const fetchWallets = () => {
    fetch(`${API_URL}/api/admin/wallet/dashboard`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : []).then(d => setWallets(d)).catch(() => {}).finally(() => setLoading(false));
  };

  const fetchPending = () => {
    if (!canManageWallet) return;
    fetch(`${API_URL}/api/admin/wallet/pending`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : []).then(d => setPending(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_URL}/api/admin/wallet/reconcile-status`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : null).then(d => setReconcileStatus(d)).catch(() => {});
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/wallet/reconcile`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Sync failed');
      const s = d.summary || {};
      toast.success(s.credited ? `Synced — ${s.credited} top-up(s) auto-credited` : 'Synced — no new payments found');
      fetchPending();
      fetchWallets();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const timeAgo = (iso) => {
    if (!iso) return 'never';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    return `${Math.floor(diff / 86400)} d ago`;
  };

  useEffect(() => { fetchWallets(); fetchPending(); }, [token]); // eslint-disable-line

  const submitAction = async () => {
    if (!actionDialog || !actionReason.trim()) {
      toast.error('A reason / proof is required');
      return;
    }
    setProcessing(true);
    try {
      const { txn, mode } = actionDialog;
      const r = await fetch(`${API_URL}/api/admin/wallet/pending/${txn.transaction_id}/${mode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: actionReason.trim() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message || (mode === 'approve' ? 'Top-up approved' : 'Top-up rejected'));
      setActionDialog(null);
      setActionReason('');
      fetchPending();
      fetchWallets();
    } catch (e) { toast.error(e.message); }
    finally { setProcessing(false); }
  };

  const handleCredit = async () => {
    if (!creditDialog || !creditAmount || !creditReason.trim()) {
      toast.error('Amount and reason/proof are required');
      return;
    }
    setCrediting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/wallet/credit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: creditDialog.user_id, amount: parseFloat(creditAmount), reason: creditReason.trim() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      setCreditDialog(null);
      setCreditAmount('');
      setCreditReason('');
      fetchWallets();
    } catch (e) { toast.error(e.message); }
    finally { setCrediting(false); }
  };

  const viewTransactions = async (w) => {
    setTxnDialog(w);
    setTxnLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/wallet/transactions/${w.user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setTxnData(await r.json());
    } catch (e) { /* silent */ }
    finally { setTxnLoading(false); }
  };

  const downloadStatement = (userId) => {
    window.open(`${API_URL}/api/admin/wallet/statement/${userId}?token=${token}`, '_blank');
  };

  const filtered = wallets.filter(w => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (w.name || '').toLowerCase().includes(q) || (w.email || '').toLowerCase().includes(q);
  });

  const totalBalance = wallets.reduce((s, w) => s + (w.balance || 0), 0);
  const totalTopup = wallets.reduce((s, w) => s + (w.total_topup || 0), 0);
  const totalSpent = wallets.reduce((s, w) => s + (w.total_spent || 0), 0);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-wallets">
      <div className="mb-6">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-secondary-900">Wallet Deposits</h1>
        <p className="text-sm text-muted-foreground">Track customer wallets, credit overrides, and download statements</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center"><Wallet className="h-5 w-5 text-green-600" /></div>
          <div><p className="text-2xl font-bold">R{totalBalance.toFixed(0)}</p><p className="text-xs text-muted-foreground">Total balances held</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><ArrowDownLeft className="h-5 w-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">R{totalTopup.toFixed(0)}</p><p className="text-xs text-muted-foreground">Total deposited</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center"><ArrowUpRight className="h-5 w-5 text-red-600" /></div>
          <div><p className="text-2xl font-bold">R{totalSpent.toFixed(0)}</p><p className="text-xs text-muted-foreground">Total spent</p></div>
        </CardContent></Card>
      </div>

      {/* Auto-sync status bar */}
      {canManageWallet && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 px-3 py-2 rounded-lg bg-muted/40 border" data-testid="wallet-sync-bar">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <RefreshCw className="h-3.5 w-3.5" />
            <span>Top-ups auto-sync with Paystack every 2 min · last synced <strong className="text-foreground">{timeAgo(reconcileStatus?.last_run)}</strong></span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleSyncNow} disabled={syncing} data-testid="sync-now-btn">
            {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Sync now
          </Button>
        </div>
      )}

      {/* Pending Top-ups awaiting confirmation */}
      {canManageWallet && pending.length > 0 && (
        <Card className="mb-6 border-amber-200" data-testid="pending-topups-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-amber-600" />
              Pending Top-ups
              <Badge className="bg-amber-100 text-amber-700 border-0">{pending.length}</Badge>
            </CardTitle>
            <CardDescription>Confirm the customer actually paid, then approve to credit their wallet. Approving with a reason creates an audit trail.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y border-t">
              {pending.map(p => (
                <div key={p.transaction_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`pending-row-${p.transaction_id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{p.name} <span className="text-amber-700 font-bold">· R{(p.amount || 0).toFixed(2)}</span></p>
                    <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Ref: {p.payment_reference || '—'} · {p.created_at ? new Date(p.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => { setActionDialog({ txn: p, mode: 'reject' }); setActionReason(''); }}
                      data-testid={`reject-topup-${p.transaction_id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                    <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700"
                      onClick={() => { setActionDialog({ txn: p, mode: 'approve' }); setActionReason(''); }}
                      data-testid={`approve-topup-${p.transaction_id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve & Credit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." className="pl-10" data-testid="wallet-search" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">Customer</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">Balance</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4 hidden md:table-cell">Deposited</th>
                <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4 hidden md:table-cell">Spent</th>
                <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">Actions</th>
              </tr></thead>
              <tbody className="divide-y">
                {filtered.map(w => (
                  <tr key={w.user_id} className="hover:bg-muted/30">
                    <td className="py-3 px-4">
                      <p className="font-medium text-sm">{w.name}</p>
                      <p className="text-xs text-muted-foreground">{w.email}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-bold text-sm ${w.balance > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>R{w.balance.toFixed(2)}</span>
                    </td>
                    <td className="py-3 px-4 text-right hidden md:table-cell text-sm text-muted-foreground">R{w.total_topup.toFixed(0)}</td>
                    <td className="py-3 px-4 text-right hidden md:table-cell text-sm text-muted-foreground">R{w.total_spent.toFixed(0)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => viewTransactions(w)} title="View transactions">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => downloadStatement(w.user_id)} title="Download statement">
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        {canManageWallet && (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-green-600 border-green-200 hover:bg-green-50" onClick={() => setCreditDialog(w)} title="Credit wallet">
                            <Plus className="h-3.5 w-3.5 mr-0.5" /> Credit
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-muted-foreground">{search ? 'No wallets match' : 'No wallets yet'}</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Approve / Reject Pending Top-up Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={open => { if (!open) { setActionDialog(null); setActionReason(''); } }}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog?.mode === 'approve'
                ? <><CheckCircle2 className="h-5 w-5 text-green-600" /> Approve Top-up</>
                : <><XCircle className="h-5 w-5 text-red-600" /> Reject Top-up</>}
            </DialogTitle>
          </DialogHeader>
          {actionDialog && (
            <div className="space-y-4 pt-2">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm">{actionDialog.txn.name} · R{(actionDialog.txn.amount || 0).toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{actionDialog.txn.email}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Ref: {actionDialog.txn.payment_reference || '—'}</p>
              </div>
              {actionDialog.mode === 'approve' && (
                <div className="bg-green-50 border border-green-100 rounded-lg p-2.5 text-xs text-green-800">
                  This will credit R{(actionDialog.txn.amount || 0).toFixed(2)} to the customer's wallet so they can spend it immediately. Only approve if you have confirmed the payment was received.
                </div>
              )}
              <div>
                <label className="text-sm font-medium block mb-1">{actionDialog.mode === 'approve' ? 'Proof / Reason *' : 'Reason for rejection *'}</label>
                <textarea
                  value={actionReason} onChange={e => setActionReason(e.target.value)}
                  placeholder={actionDialog.mode === 'approve'
                    ? 'e.g. Paystack ref confirmed in dashboard, EFT received in bank account, cash paid at counter...'
                    : 'e.g. No matching payment found, duplicate top-up, customer cancelled...'}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary/20 outline-none"
                  data-testid="action-reason"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Stored as an audit trail and visible in the customer statement.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionDialog(null); setActionReason(''); }}>Cancel</Button>
            <Button onClick={submitAction} disabled={processing || !actionReason.trim()}
              className={actionDialog?.mode === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-action">
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : (actionDialog?.mode === 'approve' ? <CheckCircle2 className="h-4 w-4 mr-1" /> : <XCircle className="h-4 w-4 mr-1" />)}
              {actionDialog?.mode === 'approve' ? `Approve & Credit R${(actionDialog?.txn?.amount || 0).toFixed(2)}` : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Override Dialog */}
      <Dialog open={!!creditDialog} onOpenChange={open => { if (!open) setCreditDialog(null); }}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-green-600" /> Credit Wallet Override</DialogTitle></DialogHeader>
          {creditDialog && (
            <div className="space-y-4 pt-2">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm">{creditDialog.name}</p>
                <p className="text-xs text-muted-foreground">{creditDialog.email} · Current balance: R{creditDialog.balance.toFixed(2)}</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Amount to credit (R) *</label>
                <Input type="number" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} placeholder="0.00" min="1" data-testid="credit-amount" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Proof / Reason *</label>
                <textarea
                  value={creditReason} onChange={e => setCreditReason(e.target.value)}
                  placeholder="e.g. Paystack ref TXN_123456 confirmed in dashboard, EFT receipt attached, cash received at counter..."
                  rows={3}
                  className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary/20 outline-none"
                  data-testid="credit-reason"
                />
                <p className="text-[10px] text-muted-foreground mt-1">This is stored as an audit trail and visible in the statement</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialog(null)}>Cancel</Button>
            <Button onClick={handleCredit} disabled={crediting || !creditAmount || !creditReason.trim()} className="bg-green-600 hover:bg-green-700" data-testid="confirm-credit">
              {crediting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Credit R{creditAmount || '0'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={!!txnDialog} onOpenChange={open => { if (!open) { setTxnDialog(null); setTxnData(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Wallet History — {txnDialog?.name}</DialogTitle>
          </DialogHeader>
          {txnLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : txnData ? (
            <div className="space-y-3 pt-2">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600">Current Balance</p>
                <p className="text-2xl font-bold text-green-700">R{txnData.balance.toFixed(2)}</p>
              </div>
              <div className="divide-y">
                {txnData.transactions.map((t, idx) => (
                  <div key={t.transaction_id || idx} className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                        t.type === 'topup' || t.type === 'admin_credit' ? 'bg-green-100' : t.type === 'refund_request' ? 'bg-amber-100' : 'bg-red-100'
                      }`}>
                        {t.type === 'topup' || t.type === 'admin_credit' ? <ArrowDownLeft className="h-3.5 w-3.5 text-green-600" /> : <ArrowUpRight className="h-3.5 w-3.5 text-red-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t.type === 'admin_credit' ? 'Admin Credit' : t.type === 'topup' ? 'Top Up' : t.type === 'payment' ? 'Order Payment' : t.type?.replace(/_/g, ' ')}</p>
                        {(t.type === 'topup' || t.type === 'admin_credit') && t.status === 'completed' && (
                          t.auto_verified ? (
                            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium text-green-700 bg-green-100 rounded-full px-1.5 py-0.5" data-testid="badge-auto-synced">
                              <CheckCircle2 className="h-3 w-3" /> Auto-synced from Paystack
                            </span>
                          ) : (t.manually_approved || t.type === 'admin_credit') ? (
                            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5" data-testid="badge-manual">
                              <CreditCard className="h-3 w-3" /> Manual {t.type === 'admin_credit' ? 'credit' : 'approval'}
                            </span>
                          ) : null
                        )}
                        <p className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                        {t.reason && <p className="text-[10px] text-amber-700 mt-0.5">{t.reason}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`font-semibold text-sm ${(t.amount || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(t.amount || 0) > 0 ? '+' : ''}R{Math.abs(t.amount || 0).toFixed(2)}
                      </span>
                      <Badge variant="secondary" className="text-[9px] ml-1">{t.status}</Badge>
                    </div>
                  </div>
                ))}
                {txnData.transactions.length === 0 && <p className="text-center text-muted-foreground py-6 text-sm">No transactions</p>}
              </div>
              <Button variant="outline" className="w-full" onClick={() => downloadStatement(txnDialog.user_id)}>
                <Download className="h-4 w-4 mr-2" /> Download Statement (CSV)
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
